import { Center } from '@chakra-ui/react';
import { useAuthentication } from "hooks/useAuthentication";
import { usePermissions } from "hooks/usePermissions";
import { useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { ROUTE_PRIORITY } from "config/routes";

type GuardProps = {
  children: JSX.Element | JSX.Element[];
  allowRoles?: string[];
};

export function Authenticated({ children, allowRoles }: GuardProps) {
  const { user } = useAuthentication();
  
  if (user) {
    return <>{children}</>;
  }

  return (
    <Center>
      Non autorisé <a href="/">cliquez ici pour continuer</a>
    </Center>
  );
}

export function UnAuthenticated({ children, allowRoles }: GuardProps) {
  const { user } = useAuthentication();
  const { permissions } = usePermissions();
  
  if (user) {
    // Attendre que les permissions soient chargées avant de rediriger
    if (permissions.length === 0) {
      return <Center>Chargement des permissions...</Center>;
    }
    
    // Fonction pour vérifier les permissions
    const checkPermission = (permission: string) => {
      const requestedPermission = permission.toLowerCase().trim();
      
      return permissions.some((p: any) => {
        if (!p.model || !p.permission) return false;
        
        const fullPermission = `${p.permission} ${p.model}`.toLowerCase();
        const actionOnly = p.permission.toLowerCase();
        
        return fullPermission === requestedPermission || actionOnly === requestedPermission;
      });
    };

    // Trouver la première route pour laquelle l'utilisateur a la permission
    for (const route of ROUTE_PRIORITY) {
      if (checkPermission(route.permission)) {
        return <Navigate to={route.path} />;
      }
    }

    // Fallback vers le dashboard selon le rôle
    // Normaliser le rôle (supprimer les espaces et convertir en minuscules)
    const userRole = user?.user?.roles?.toLowerCase().replace(/\s/g, '') || '';
    const taxDashboardRoles = [
      "taxsuperadmin",
      "taxadmin", 
      "taxcollector",
      "taxsupervisor",
      "dealer",
      "taxpayer",
      "debitor"
    ];
    
    if (userRole && taxDashboardRoles.includes(userRole)) {
      return <Navigate to="/tax/dashboard" />;
    }
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
}

export function AnyUserWithPermission({ children, allowRoles }: GuardProps) {
  const { loading, hasRole } = useAuthentication();

  if (loading) {
    return <Center>Loading...</Center>;
  }

  if (allowRoles?.some(hasRole)) {
    return <>{children}</>;
  }

  return (
    <Center>
      Non autorisé <a href="/">cliquez ici pour continuer</a>
    </Center>
  );
}

export function PropertyOwnerOnlyGuard({ children, allowRoles }: GuardProps) {
  const {
    user: { user, auth } = {},
    authProvider: { signOut },
  } = useAuthentication();

  const checkAccessTokenExpiration = useCallback(async () => {
    const expirationDate = new Date(auth?.tokenExpirationDate as string);
    const now = new Date();
    if (!user || now > expirationDate) {
      await signOut();
    }
  }, [signOut, auth, user]);

  useEffect(() => {
    checkAccessTokenExpiration();
  }, [checkAccessTokenExpiration]);
  // console.log(allowRoles?.some(hasRole));
  // IMPORTANT: Customer user account should not be able to access this page
  const userIsCustomer = user && user?.id;
  if (
    userIsCustomer &&
    allowRoles?.some((item) => user?.roles.includes(item))
  ) {
    return <>{children}</>;
  }

  return (
    <Center>
      Non autorisé <a href="/">cliquez ici pour continuer</a>
    </Center>
  );
}

export function AdminOnlyGuard({ children, allowRoles }: GuardProps) {
  const {
    user: { user, auth } = {},
    authProvider: { signOut },
  } = useAuthentication();
  console.log(user);

  const checkAccessTokenExpiration = useCallback(async () => {
    const expirationDate = new Date(auth?.tokenExpirationDate as string);
    const now = new Date();
    if (!user || now > expirationDate) {
      await signOut();
    }
  }, [signOut, auth, user]);

  useEffect(() => {
    checkAccessTokenExpiration();
  }, [checkAccessTokenExpiration]);
  // console.log(allowRoles?.some(hasRole));
  // IMPORTANT: Customer user account should not be able to access this page
  const userIsCustomer = user && user?.id;
  if (
    userIsCustomer &&
    allowRoles?.some((item) => user?.roles.includes(item))
  ) {
    return <>{children}</>;
  }

  return (
    <Center>
      Non autorisé <a href="/">cliquez ici pour continuer</a>
    </Center>
  );
}
